// src/components/Reports/ReportSelect.tsx
// Shared single-select for Report Viewer widgets — same style as AttendanceReport

import Select, { type StylesConfig } from 'react-select';

export interface ReportSelectOption {
  value: string | number;
  label: string;
}

const buildSelectStyles = <TOption, >(): StylesConfig<TOption, false> =>
{
  const isDark = document.documentElement.classList.contains('dark');
  return {
    control: base => ({
      ...base,
      minHeight: 30,
      fontSize: 11,
      borderColor: isDark ? '#4a4c4c' : '#ccc',
      backgroundColor: isDark ? '#3a3c3c' : base.backgroundColor,
      boxShadow: 'none',
      borderRadius: 4,
      '&:hover': { borderColor: isDark ? '#6e6860' : '#aaa' },
    }),
    menu: base => ({
      ...base,
      fontSize: 11,
      zIndex: 10001,
      borderRadius: 2,
      marginTop: 2,
      backgroundColor: isDark ? '#333535' : base.backgroundColor,
    }),
    menuPortal: base => ({ ...base, zIndex: 10001 }),
    menuList: base => ({ ...base, padding: 0 }),
    valueContainer: base => ({ ...base, padding: '2px 6px' }),
    indicatorsContainer: base => ({ ...base, height: 30 }),
    indicatorSeparator: () => ({ display: 'none' }),
    clearIndicator: base => ({
      ...base,
      padding: '0 6px',
      color: isDark ? '#6e6860' : '#999',
      cursor: 'pointer',
    }),
    dropdownIndicator: base => ({ ...base, padding: '0 6px', color: isDark ? '#6e6860' : '#999' }),
    singleValue: base => ({ ...base, color: isDark ? '#a9a093' : base.color }),
    input: base => ({ ...base, color: isDark ? '#a9a093' : base.color }),
    placeholder: base => ({ ...base, color: isDark ? '#6e6860' : base.color }),
    option: (base, state) => ({
      ...base,
      backgroundColor:
        state.isSelected || state.isFocused ? '#62accb' : isDark ? '#333535' : 'transparent',
      color: state.isSelected || state.isFocused ? '#fff' : isDark ? '#a9a093' : '#333',
      fontSize: 11,
      padding: '5px 10px',
      cursor: 'pointer',
      ':active': { backgroundColor: '#508ca6', color: '#fff' },
    }),
  };
};

interface ReportSelectProps<TOption = ReportSelectOption> {
  options: TOption[];
  value: TOption | null | undefined;
  onChange: (value: TOption | null) => void;
  label?: string;
  width?: number;
  isClearable?: boolean;
  placeholder?: string;
  getOptionLabel?: (option: TOption) => string;
  getOptionValue?: (option: TOption) => string;
  isMulti?: false;
  isSearchable?: boolean;
  autoFocus?: boolean;
  openMenuOnFocus?: boolean;
  closeMenuOnSelect?: boolean;
  styles?: StylesConfig<TOption, false>;
}

export function ReportSelect<TOption = ReportSelectOption>({
  options,
  value,
  onChange,
  label,
  width = 130,
  isClearable = false,
  placeholder = 'Select...',
  getOptionLabel,
  getOptionValue,
  isSearchable = false,
  autoFocus,
  openMenuOnFocus,
  closeMenuOnSelect,
  styles,
}: ReportSelectProps<TOption>)
{
  const mergedStyles: StylesConfig<TOption, false> = {
    ...buildSelectStyles<TOption>(),
    ...styles,
  };

  return (
    <div className="burndownchart_select" style={{ gap: 4 }}>
      {label && <span className="burndownchart_Label">{label}</span>}
      <div style={{ width }}>
        <Select<TOption, false>
          options={options}
          value={value ?? null}
          onChange={onChange}
          styles={mergedStyles}
          isClearable={isClearable}
          isSearchable={isSearchable}
          placeholder={placeholder}
          menuPortalTarget={document.body}
          menuPosition="fixed"
          getOptionLabel={getOptionLabel}
          getOptionValue={getOptionValue}
          autoFocus={autoFocus}
          openMenuOnFocus={openMenuOnFocus}
          closeMenuOnSelect={closeMenuOnSelect}
        />
      </div>
    </div>
  );
}

export default ReportSelect;
